import { Injectable } from '@angular/core';
import API, { graphqlOperation } from '@aws-amplify/api-graphql';
import { BehaviorSubject } from 'rxjs';

export interface Employee {
  id: string;
  firstName: string;
  lastName: string;
  fullName?: string;
  birthDate?: string;
  birthYear?: number;
  jmbg?: string;
  address?: string;
  residence?: string;
  municipalityCode?: string;
  startDate?: string;
  endDate?: string;
  taxCard?: string;
  grossSalary?: number;
  searchKeyword?: string;
  clientID: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateEmployeeInput {
  firstName: string;
  lastName: string;
  idNumber?: string;
  birthDate?: string;
  birthYear?: number;
  jmbg?: string;
  address?: string;
  residence?: string;
  municipalityCode?: string;
  startDate?: string;
  endDate?: string;
  taxCard?: string;
  grossSalary?: number;
  clientID: string;
}

export interface UpdateEmployeeInput {
  id: string;
  firstName?: string;
  lastName?: string;
  birthDate?: string;
  birthYear?: number;
  jmbg?: string;
  address?: string;
  residence?: string;
  municipalityCode?: string;
  startDate?: string;
  endDate?: string;
  taxCard?: string;
  grossSalary?: number;
}

@Injectable({
  providedIn: 'root'
})
export class EmployeeDataService {
  private employeesSubject = new BehaviorSubject<Employee[]>([]);
  public employees$ = this.employeesSubject.asObservable();
  
  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();

  constructor() { }

  /**
   * List all employees for a client
   */
  async listEmployees(clientID: string, nextToken?: string): Promise<{ items: Employee[]; nextToken?: string }> {
    const statement = `
      query ListEmployeesByClientID($clientID: ID!, $limit: Int, $nextToken: String) {
        listEmployeesByClientID(clientID: $clientID, limit: $limit, nextToken: $nextToken) {
          items {
            id
            firstName
            lastName
            birthDate
            birthYear
            jmbg
            address
            residence
            municipalityCode
            startDate
            endDate
            taxCard
            grossSalary
            searchKeyword
            clientID
            createdAt
            updatedAt
          }
          nextToken
        }
      }
    `;

    this.loadingSubject.next(true);

    try {
      const response = (await API.graphql(
        graphqlOperation(statement, { clientID, limit: 100, nextToken })
      )) as any;
      
      const result = response.data.listEmployeesByClientID || { items: [], nextToken: null };
      const employees = (result.items || []).map((emp: Employee) => ({
        ...emp,
        fullName: `${emp.firstName} ${emp.lastName}`
      }));
      
      this.employeesSubject.next(employees);
      this.loadingSubject.next(false);
      
      return { items: employees, nextToken: result.nextToken };
    } catch (error) {
      console.error('Error listing employees:', error);
      this.loadingSubject.next(false);
      throw error;
    }
  }

  /**
   * Get a single employee by ID
   */
  async getEmployee(id: string): Promise<Employee> {
    const statement = `
      query GetEmployee($id: ID!) {
        getEmployee(id: $id) {
          id
          firstName
          lastName
          birthDate
          birthYear
          jmbg
          address
          residence
          municipalityCode
          startDate
          endDate
          taxCard
          grossSalary
          searchKeyword
          clientID
          createdAt
          updatedAt
        }
      }
    `;

    try {
      const response = (await API.graphql(
        graphqlOperation(statement, { id })
      )) as any;
      
      const employee = response.data.getEmployee;
      return {
        ...employee,
        fullName: `${employee.firstName} ${employee.lastName}`
      };
    } catch (error) {
      console.error('Error getting employee:', error);
      throw error;
    }
  }

  /**
   * Create a new employee
   */
  async createEmployee(input: CreateEmployeeInput): Promise<Employee> {
    const statement = `
      mutation CreateEmployee($input: CreateEmployeeInput!) {
        createEmployee(input: $input) {
          id
          firstName
          lastName
          birthDate
          birthYear
          jmbg
          address
          residence
          municipalityCode
          startDate
          endDate
          taxCard
          grossSalary
          searchKeyword
          clientID
          createdAt
          updatedAt
        }
      }
    `;

    try {
      const response = (await API.graphql(
        graphqlOperation(statement, { input })
      )) as any;
      
      const newEmployee = response.data.createEmployee;
      
      if (!newEmployee) {
        console.error('createEmployee returned null. Response:', JSON.stringify(response));
        throw new Error('Employee creation returned null - check resolver');
      }

      // Update local state
      const currentEmployees = this.employeesSubject.getValue();
      this.employeesSubject.next([...currentEmployees, {
        ...newEmployee,
        fullName: `${newEmployee.firstName} ${newEmployee.lastName}`
      }]);
      
      return newEmployee;
    } catch (error) {
      console.error('Error creating employee:', error);
      throw error;
    }
  }

  /**
   * Update an existing employee
   */
  async updateEmployee(input: UpdateEmployeeInput): Promise<Employee> {
    const statement = `
      mutation UpdateEmployee($input: UpdateEmployeeInput!) {
        updateEmployee(input: $input) {
          id
          firstName
          lastName
          birthDate
          birthYear
          jmbg
          address
          residence
          municipalityCode
          startDate
          endDate
          taxCard
          grossSalary
          searchKeyword
          clientID
          createdAt
          updatedAt
        }
      }
    `;

    try {
      const response = (await API.graphql(
        graphqlOperation(statement, { input })
      )) as any;
      
      const updatedEmployee = response.data.updateEmployee;
      
      // Update local state
      const currentEmployees = this.employeesSubject.getValue();
      const updatedList = currentEmployees.map(emp => 
        emp.id === updatedEmployee.id 
          ? { ...updatedEmployee, fullName: `${updatedEmployee.firstName} ${updatedEmployee.lastName}` }
          : emp
      );
      this.employeesSubject.next(updatedList);
      
      return updatedEmployee;
    } catch (error) {
      console.error('Error updating employee:', error);
      throw error;
    }
  }

  /**
   * Delete an employee
   */
  async deleteEmployee(id: string): Promise<void> {
    const statement = `
      mutation DeleteEmployee($input: DeleteEmployeeInput!) {
        deleteEmployee(input: $input) {
          id
        }
      }
    `;

    try {
      await API.graphql(
        graphqlOperation(statement, { input: { id } })
      );
      
      // Update local state
      const currentEmployees = this.employeesSubject.getValue();
      this.employeesSubject.next(currentEmployees.filter(emp => emp.id !== id));
    } catch (error) {
      console.error('Error deleting employee:', error);
      throw error;
    }
  }

  /**
   * Search employees by name
   */
  searchEmployees(query: string): Employee[] {
    const employees = this.employeesSubject.getValue();
    const lowerQuery = query.toLowerCase();
    
    return employees.filter(emp => 
      emp.firstName?.toLowerCase().includes(lowerQuery) ||
      emp.lastName?.toLowerCase().includes(lowerQuery) ||
      emp.fullName?.toLowerCase().includes(lowerQuery) ||
      emp.jmbg?.toLowerCase().includes(lowerQuery)
    );
  }

  /**
   * Calculate net salary from gross salary
   * Based on typical Bosnian tax rates (can be customized)
   */
  calculateNetSalary(grossSalary: number, taxRate: number = 10, 
                      healthInsurance: number = 12.5, 
                      pensionInsurance: number = 17, 
                      unemploymentInsurance: number = 1.5): number {
    const totalDeductions = taxRate + healthInsurance + pensionInsurance + unemploymentInsurance;
    const deductionAmount = grossSalary * (totalDeductions / 100);
    return Math.round((grossSalary - deductionAmount) * 100) / 100;
  }
}
